/**
 * v2 typography — Geist typeface + type scale.
 *
 * The type scale maps directly to MUI variant names where possible.
 * Ad-hoc display text and tabular numerals are handled via the exported
 * sx helpers (displaySx, numericSx) rather than per-component overrides.
 *
 * Size reference (from DESIGN-TOKENS.md):
 *   h1 (page title)   26px / 500 / -0.02em
 *   h2 (section title) 22px / 500
 *   h3                 16px / 600
 *   h4                 14px / 600
 *   h5 (panel title)   13px / 600
 *   h6                 12.5px / 500
 *   body1              14px / 400  (general UI)
 *   body2              12.5px / 400  (v2 default text)
 *   caption            11.5px / 400  (hint / meta)
 */

import type { CSSObject, SxProps } from '@mui/material/styles';

// `@fontsource-variable/*` (imported in `main.tsx`) registers the families as
// "Geist Variable" / "Geist Mono Variable"; a stack naming plain Geist falls
// back to the system font. Why they are self-hosted: docs/architecture/design-system.md.
export const GEIST = '"Geist Variable", -apple-system, system-ui, sans-serif';
export const GEIST_MONO = '"Geist Mono Variable", ui-monospace, monospace';

export const typographyOptions = {
  fontFamily: GEIST,
  fontWeightLight:   300,
  fontWeightRegular: 400,
  fontWeightMedium:  500,
  fontWeightBold:    700,

  h1: {
    fontFamily:    GEIST,
    fontSize:      '1.625rem',   // 26px
    fontWeight:    500,
    lineHeight:    1.25,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily:    GEIST,
    fontSize:      '1.375rem',   // 22px
    fontWeight:    500,
    lineHeight:    1.3,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontFamily: GEIST,
    fontSize:   '1rem',          // 16px
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h4: {
    fontFamily: GEIST,
    fontSize:   '0.875rem',      // 14px
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontFamily: GEIST,
    fontSize:   '0.8125rem',     // 13px — panel title
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h6: {
    fontFamily: GEIST,
    fontSize:   '0.78125rem',    // 12.5px
    fontWeight: 500,
    lineHeight: 1.56,
  },
  body1: {
    fontFamily: GEIST,
    fontSize:   '0.875rem',      // 14px — general UI
    lineHeight: 1.6,
  },
  body2: {
    fontFamily: GEIST,
    fontSize:   '0.78125rem',    // 12.5px — v2 default text
    lineHeight: 1.6,
  },
  caption: {
    fontFamily: GEIST,
    fontSize:   '0.71875rem',    // 11.5px
    lineHeight: 1.5,
  },
  button: {
    fontFamily:    GEIST,
    fontSize:      '0.78125rem', // 12.5px
    fontWeight:    500,
    textTransform: 'none' as const,
  },
  overline: {
    fontFamily:    GEIST,
    fontSize:      '0.71875rem', // 11.5px — KPI labels (raised from spec's 11px to meet a11y minimum)
    fontWeight:    600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
  },
};

/**
 * The smallest type allowed anywhere — `docs/architecture/design-system.md`
 * states it as a rule, so it lives with the type scale rather than being
 * re-declared per feature. `timesheets/designTokens.ts` re-exports this under
 * its long-standing name.
 */
export const MIN_FONT_SIZE = '0.7rem';

/**
 * Apply to display headings and KPI values for the Geist display treatment.
 * letterSpacing: -0.025em gives the tight optical kerning seen in the prototype.
 *
 * Typed as `CSSObject` (the inner shape `SxProps` accepts) so
 * the helper composes cleanly inside `sx={[...]}` arrays as well as
 * standalone. `SxProps<Theme>` includes an array variant, which the sx
 * prop's overloaded type rejects when nested inside another array.
 */
export const displaySx: CSSObject = {
  fontFamily: GEIST,
  fontWeight: 500,
  letterSpacing: '-0.025em',
};

/**
 * Apply to every numeric value (KPIs, totals, percentages, dates).
 * Enables tabular figures so numbers align in columns without explicit widths.
 * Optionally combine with Geist Mono for a monospace numeral style.
 */
export const numericSx: CSSObject = {
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Geist Mono sx helper — apply to inline code, date strings, or any
 * value that benefits from a monospace numeral feel.
 */
export const monoSx: CSSObject = {
  fontFamily: GEIST_MONO,
  fontVariantNumeric: 'tabular-nums',
};

// Re-export SxProps to keep the public surface intact for any external
// consumer that imports the type alongside these helpers.
export type { SxProps };
